import { computed, ref, shallowRef, watch } from "vue";
import axios from "axios";
import router from "@/router";
import store from "@/store";

const userProfile = shallowRef(null);
const profileError = ref(null);
const isProfileLoading = ref(false);
const profileLoaded = ref(false);

const isAuthenticated = computed(() => {
  const storeAuth = store.state?.autentication?.isAuth;
  if (typeof storeAuth === "boolean") {
    return storeAuth;
  }
  return !!localStorage.getItem("token");
});

const requireLogin = ({ redirect } = {}) => {
  const currentRoute = router.currentRoute.value;
  const target =
    redirect ||
    (currentRoute?.name && currentRoute.name !== "login" ? currentRoute.fullPath : "/");

  const query = target ? { redirect: target } : {};

  router.push({
    name: "login",
    query,
  });
};

const resetProfileState = () => {
  userProfile.value = null;
  profileError.value = null;
  profileLoaded.value = false;
};

watch(
  isAuthenticated,
  (isAuth) => {
    if (!isAuth) {
      resetProfileState();
    }
  },
  { immediate: true }
);

const fetchUserProfile = async ({ force } = {}) => {
  if (!isAuthenticated.value) {
    return null;
  }

  if (profileLoaded.value && !force) {
    return userProfile.value;
  }

  isProfileLoading.value = true;
  profileError.value = null;

  try {
    const { data } = await axios.get("/api/me");
    const profile = data?.data || data || null;
    userProfile.value = profile;
    profileLoaded.value = true;
    return profile;
  } catch (error) {
    profileError.value = error;
    if (error?.response?.status === 401) {
      requireLogin();
    }
    throw error;
  } finally {
    isProfileLoading.value = false;
  }
};

export const useAuth = () => ({
  isAuthenticated,
  userProfile,
  profileError,
  isProfileLoading,
  fetchUserProfile,
  requireLogin,
});

export default useAuth;
